import BaseView from "../core/views/BaseView";
import C_PointerEvt from "../core/const/C_PointerEvt";
import C_GameEvt from "../core/const/C_GameEvent";
import C_Snd from "../const/C_Snd";
import MathUtil from "../core/utils/MathUtil";
import Point3D from "../core/math/Point3D";
import Button from "../core/ui/Button";
import GameOverWin from "./GameOverWin";
import ConfirmWin from "./ConfirmWin";
import C_ViewType from "../const/C_ViewType";
import C_Local from "../core/const/C_Local";
import C_CockpitParts from "../const/C_CockpitParts";
import GameSprite from "../core/components/GameSprite";
import Physics from "../core/components/Physics";
import CoinsAnimations from "../core/model/CoinsAnimations";
import FriendlyBot from "../core/components/FriendlyBot";


class MainView extends BaseView
{

    constructor()
    {
        super();

        document.body.style.cursor = "none";
        this.interactive = true;
        this.autoFireOn = false;
        this.targetLocked = false;

        this.shakeAmplitude = this.app.layout.relWidth(.2);

        this.shipId = this.model.getRoomId() + 1;

        this.addBackground();
        this.addContainers();
        this.addCockpit();
        this.addPlayerBalance();
        // this.addBotsBalance();
        this.addButtons();
        this.addFireNotification();
        this.addReticle();
        this.addViewListeners();

        let minDel = 5;
        let maxDel = 10;
        this.addDelayedCall(MathUtil.getRandomInt(minDel, maxDel), this.playRandomSound, [], this);

        this.mainContainer.x -= this.bg.width * 1/12;
        this.mainContainer.y -= this.bg.height * 1/12;

        this.playEnterFadeOut();
    }


    playEnterFadeOut()
    {
        let overlay = new PIXI.Graphics();
        overlay.beginFill(0xffffff);
        overlay.drawRect(0, 0, this.width, this.height);
        this.addChild(overlay);

        TweenMax.to(overlay, 2.5, {alpha: 0, delay: 0.5});
        TweenMax.delayedCall(4.5, this.removeChild, [overlay], this);
    }


    addBackground()
    {
        this.bg = this.assets.getSprite("space_bg");
        let bgTint = [0xffffff, 0xf29d9d, 0xfffd91];

        let electricityTextures = this.assets.generateFrames("spaceship_electricity_", 3, 0, 11);
        this.elecricityAnim = new PIXI.extras.AnimatedSprite(electricityTextures);
        this.elecricityAnim.animationSpeed = 0.2;
        this.elecricityAnim.play();
        this.bg.addChild(this.elecricityAnim);

        let lasersTextures = this.assets.generateFrames("laser_", 0, 1, 6);
        this.lasersAnim = new PIXI.extras.AnimatedSprite(lasersTextures);
        this.lasersAnim.animationSpeed = 0.7;
        this.lasersAnim.onLoop = this.onLaserLoop.bind(this);
        this.playLaserAnim();
        this.bg.addChild(this.lasersAnim);

        this.layout.propHeightScale(this.bg, 100 + this.config.viewPadSize);
        this.layout.verticalAlignMiddle(this.bg);
        this.layout.horizontalAlignCenter(this.bg);
        this.addChild(this.bg);

        this.bg.tint = bgTint[this.shipId - 1];

        // Used to update BG and mainContainer positions
        this.deltaX = -(this.bg.width - this.layout.rect.width);
        this.deltaY = -(this.bg.height - this.layout.rect.height);
    }


    addContainers()
    {
        this.mainContainer = new PIXI.Container();
        this.addChild(this.mainContainer);

        this.addShield();

        this.botsContainer = new PIXI.Container();
        this.addChild(this.botsContainer);

        this.cockpit = new PIXI.Container();
        this.addChild(this.cockpit);
    }


    addShield()
    {
        this.layout.propHeightScale(this.shield, 100);
        this.addChild(this.shield);
        this.shield.alpha = 0.5;
    }


    addCockpit()
    {
        // The size of the exported assets, needed for calculating the position of some elements
        let cockpitWidth = 1920;
        let cockpitHeight = 1080;

        let numCockpitParts = this.partsPerShip[this.shipId];


        // TODO create own class for weapons and add the functions from Game Controller there too
        this.weapons = {};
        this.onShootAnimations = [];
        this.onShootGlows = [];

        for (let cockpitIdx = 0; cockpitIdx < numCockpitParts.length; cockpitIdx++)
        {
            let part = numCockpitParts[cockpitIdx];
            let cockpitPart;
            if (part.glow)
            {
                if (part.tweenType == "timeline")
                {
                    let tl = new TimelineMax({repeat: -1});
                    cockpitPart = new PIXI.Container();
                    let partSprite;
                    for (let p = 1; p <= part.parts; p++)
                    {
                        partSprite = this.assets.getSprite(part.name + p);
                        partSprite.alpha = 0;
                        tl.add(TweenMax.to(partSprite, 0.5 / part.parts, {alpha: 1}));
                        tl.add(TweenMax.to(partSprite, 0.5 / part.parts, {alpha: 0}));
                        cockpitPart.addChild(partSprite);
                    }

                    tl.play();
                }
                else if (part.tweenType == "loading")
                {
                    cockpitPart = new PIXI.Container();
                    let partSprite;
                    let tl = new TimelineMax({repeat: -1});
                    for (let p = 1; p <= part.parts; p++)
                    {
                        partSprite = this.assets.getSprite(part.name + p);

                        tl.add(TweenMax.to(partSprite, 0, {alpha: 0}), part.delay * p);
                        tl.add(TweenMax.to(partSprite, 0, {alpha: 1}), (part.parts + 1) * part.delay);
                        cockpitPart.addChild(partSprite);
                    }

                    tl.play();
                }
                else // normal glow
                {
                    cockpitPart = this.assets.getSprite(part.name);
                    cockpitPart.alpha = 0;
                    let endAlpha = part.alpha ? part.alpha : 1;
                    let delay = part.delay ? part.delay : 0;
                    let dur = part.duration ? part.duration : 0.5;
                    // let repeatDelay = part.repeatDelay ? part.repeatDelay * Math.random() : 0;
                    let repeatDelay = part.repeatDelay ? part.repeatDelay : 0;
                    if (part.playOnShootOnly)
                    {
                        cockpitPart.endAlpha = endAlpha;
                        cockpitPart.dur = dur;
                        cockpitPart.repeatDelay = repeatDelay;
                        this.onShootGlows.push(cockpitPart);
                    }
                    else
                    {
                        TweenMax.to(cockpitPart, dur, {
                            alpha: endAlpha,
                            delay: delay,
                            repeat: -1,
                            yoyo: true,
                            repeatDelay: repeatDelay
                        });
                    }
                }
            }
            else if (part.rotation)
            {
                cockpitPart = this.assets.getSprite(part.name);
                cockpitPart.anchor.set(part.anchor.x, part.anchor.y);
                cockpitPart.x += cockpitWidth * part.position.x;
                cockpitPart.y += cockpitHeight * part.position.y;
                cockpitPart.rotation = part.rotation;

                TweenMax.to(cockpitPart, part.duration, {
                    rotation: -part.rotation,
                    ease: Quad.easeInOut,
                    repeat: -1,
                    yoyo: true
                });
            }
            else if (part.animation)
            {
                let padLength = part.frameLength ? part.frameLength : 0;
                let textures = this.assets.generateFrames(part.name, padLength, 0, part.lastFrame);
                cockpitPart = new PIXI.extras.AnimatedSprite(textures);
                cockpitPart.animationSpeed = part.animSpeed;
                cockpitPart.replayDelay = part.replayDelay ? part.replayDelay : 0;
                if (part.playOnShootOnly)
                {
                    cockpitPart.loop = false;
                    cockpitPart.onComplete = () => {
                        cockpitPart.alpha = 0;
                        TweenMax.to(cockpitPart, 0, {alpha: 1, delay: cockpitPart.replayDelay});
                        TweenMax.delayedCall(cockpitPart.replayDelay, cockpitPart.gotoAndPlay, [0], cockpitPart);
                    };
                    cockpitPart.isPlaying = false;
                    cockpitPart.startDelay = part.startDelay ? part.startDelay : 0;
                    cockpitPart.alpha = 0;
                    this.onShootAnimations.push(cockpitPart);
                }
                else
                {
                    cockpitPart.gotoAndPlay(0);
                }
            }
            else if (part.isWeapon)
            {
                let weap;
                let weapShoot;
                if (part.shootFrame)
                {
                    cockpitPart = new PIXI.Container();
                    weap = this.assets.getSprite(part.name);
                    weapShoot = this.assets.getSprite(part.shootFrame);
                    weapShoot.alpha = 0;
                    cockpitPart.addChild(weapShoot);
                    cockpitPart.addChild(weap);
                }
                else
                {
                    cockpitPart = this.assets.getSprite(part.name);
                }


                if (part.anchor)
                {
                    cockpitPart.anchor.set(part.anchor.x, part.anchor.y);
                    // cockpitPart.pivot.x = cockpitPart.width*part.anchor.x;
                    // cockpitPart.pivot.y = cockpitPart.height*part.anchor.y;
                    cockpitPart.x += cockpitPart.width*part.anchor.x;
                    cockpitPart.y += cockpitPart.height*part.anchor.y;
                    cockpitPart.propWidth = part.anchor.x * 100;
                    cockpitPart.propHeight = part.anchor.y * 100;
                }
                this.weapons[part.weaponId] = cockpitPart;
            }
            else
            {
                cockpitPart = this.assets.getSprite(part.name);
            }

            if (cockpitPart)
            {
                cockpitPart.id = part.id;
                if (part.width && part.height)
                {
                    cockpitPart.width = part.width;
                    cockpitPart.height = part.height;
                }
                if (part.parentId)
                {
                    let parent = this.cockpit.getChildAt(part.parentId - 1);
                    // let anchor = part.anchor ? part.anchor : {x: 0, y: 0};
                    let anchor = parent.anchor ? parent.anchor : {x: 0, y: 0};
                    cockpitPart.anchor.set(anchor.x, anchor.y);
                    parent.addChild(cockpitPart);
                }
                else
                {
                    this.cockpit.addChild(cockpitPart);
                }
            }
        }

        let ampl = this.layout.relWidth(.05);

        this.layout.propHeightScale(this.cockpit, 101);
        this.cockpit.x = -this.layout.relWidth(.5);
        this.cockpit.y = -this.layout.relHeight(.5);
        this.layout.shake(this.cockpit, 1000000, ampl, ampl, 1, 0);
    }


    addPlayerBalance()
    {
        this.balanceBg = this.assets.getSprite("ingame_balance_bg");
        this.layout.propHeightScale(this.balanceBg, 7);
        this.balanceBg.anchor.set(0.5, 0);
        this.balanceBg.scale.x *= -1;
        this.layout.horizontalAlignCenter(this.balanceBg, -20);
        this.layout.verticalAlignBottom(this.balanceBg, -1);
        this.addChild(this.balanceBg);

        this.balanceTF = this.assets.getTF(this.model.getBalance(), this.layout.relHeight(5));
        this.layout.horizontalAlignCenter(this.balanceTF, -40, this.balanceBg);
        this.layout.verticalAlignBottom(this.balanceTF, 0);
        this.addChild(this.balanceTF);

        this.coin = this.assets.getSprite("coin");
        this.layout.propHeightScale(this.coin, 5);
        this.coin.x = this.balanceTF.x - this.coin.width - this.layout.relWidth(1);
        this.layout.verticalAlignBottom(this.coin);
        this.addChild(this.coin);

        this.pointsWonBg = this.assets.getSprite("ingame_balance_bg");
        this.layout.propHeightScale(this.pointsWonBg, 7);
        this.pointsWonBg.anchor.set(0.5, 0);
        this.layout.horizontalAlignCenter(this.pointsWonBg, 20);
        this.layout.verticalAlignBottom(this.pointsWonBg, -1);
        this.addChild(this.pointsWonBg);

        this.pointsWonTF = this.assets.getTF("0", this.layout.relHeight(5));
        this.layout.horizontalAlignCenter(this.pointsWonTF, -60, this.pointsWonBg);
        this.layout.verticalAlignBottom(this.pointsWonTF, 0);
        this.addChild(this.pointsWonTF);
    }


    addButtons()
    {
        this.backButton = new Button("back_but", "back_but_glow", "back_but_glow");
        this.layout.propHeightScale(this.backButton, this.navButSize);
        this.layout.horizontalAlignLeft(this.backButton, this.butHorizontalPadding);
        this.layout.verticalAlignBottom(this.backButton,  this.backButBotPadding);
        this.addChild(this.backButton);
        this.backButton.specialId = 1;

        this.paytableButton = new Button("paytable_but", "paytable_but_glow", "paytable_but_glow");
        this.layout.propHeightScale(this.paytableButton, this.navButSize);
        this.layout.horizontalAlignLeft(this.paytableButton, this.butHorizontalPadding);
        this.layout.verticalAlignBottom(this.paytableButton, this.payButBotPadding);
        this.addChild(this.paytableButton);

        let soundSkin = this.localData.getBooleanValue(C_Local.SOUND_ENABLED, this.soundEnabled) ? "btnSound" : "btnSoundOff";
        this.soundButton = new Button(soundSkin, soundSkin, soundSkin);
        this.layout.propHeightScale(this.soundButton, this.navButSize);
        this.layout.horizontalAlignRight(this.soundButton, this.butHorizontalPadding);
        this.layout.verticalAlignBottom(this.soundButton, this.soundButBotPadding);
        this.addChild(this.soundButton);

        this.backButton.on(C_PointerEvt.DOWN, this.onGoMenuConfirm, this);
        this.paytableButton.on(C_PointerEvt.DOWN, this.onPaytable, this);
        this.soundButton.on(C_PointerEvt.DOWN, this.onSoundTrigger, this);
    }


    addFireNotification()
    {
        this.autoFire = this.assets.getSprite("autofireOn");
        this.addChild(this.autoFire);
        this.autoFire.alpha = 0;

        this.autoAim = this.assets.getSprite("autoaimOn");
        this.addChild(this.autoAim);
        this.autoAim.alpha = 0;
    }


    showAutoFireNotif()
    {
        if (this.controller.targetLocked)
        {
            if (!this.autoAimIsPlaying)
            {
                this.autoFireIsPlaying = false;
                TweenMax.killTweensOf(this.autoFire);
                this.autoFire.alpha = 0;

                this.autoAimIsPlaying = true;
                TweenMax.to(this.autoAim, 0.5, {alpha: 1, yoyo: true, repeat: -1, ease:Power3.easeOut});
            }

        }
        else if (this.controller.autoFireOn)
        {
            if (!this.autoFireIsPlaying)
            {
                this.autoAimIsPlaying = false;
                TweenMax.killTweensOf(this.autoAim);
                this.autoAim.alpha = 0;

                this.autoFireIsPlaying = true;
                TweenMax.to(this.autoFire, 0.5, {alpha: 1, yoyo: true, repeat: -1, ease:Power3.easeOut});
            }
        }
        else
        {
            this.autoAimIsPlaying = false;
            TweenMax.killTweensOf(this.autoAim);
            this.autoAim.alpha = 0;

            this.autoFireIsPlaying = false;
            TweenMax.killTweensOf(this.autoFire);
            this.autoFire.alpha = 0;
        }
    }


    addReticle()
    {
        this.reticle = this.assets.getSprite("reticle_" + this.shipId);
        this.reticle.anchor.set(.5, .5);
        this.layout.propHeightScale(this.reticle, 10);
        this.addChild(this.reticle);

        if (this.app.model.isMobile())
        {
            this.targetReticle = this.assets.getSprite("reticle_" + this.shipId);
            this.targetReticle.anchor.set(.5, .5);
            this.layout.propHeightScale(this.targetReticle, 10);
            this.addChild(this.targetReticle);
            this.targetReticle.alpha = 0;

            this.app.controller.setTargetReticle(this.targetReticle);
        }
    }


    getReticle()
    {
        return this.reticle;
    }


    addViewListeners()
    {
        this.on(C_PointerEvt.DOWN, this.shoot, this);
        this.on(C_PointerEvt.UP, this.stopShooting, this);
        this.on(C_PointerEvt.UPOUTSIDE, this.stopShooting, this);
        this.on(C_PointerEvt.MOVE, this.updatePositionsAndRotations, this);

        this.addListener(this.dispatcher, C_GameEvt.BALANCE_UPDATE, this.onBalanceUpdate, this);
        this.addListener(this.dispatcher, C_GameEvt.PLAYER_HIT, this.onPlayerHit, this);
        this.addListener(this.dispatcher, C_GameEvt.PLAY_COINS_ANIM, this.playCoinsAnimation, this);
        this.addListener(this.dispatcher, C_GameEvt.PLAY_FLARE_KILL_ANIM, this.onFlareKilled, this);
        this.addListener(this.dispatcher, C_GameEvt.RESCUE_POD_GOO_CLEAN, this.onPodSpecialAnim, this);
        this.addListener(this.dispatcher, C_GameEvt.FLIER_KILL, this.onExplosion, this);
        this.addListener(this.dispatcher, C_GameEvt.BOT_JOINED, this.addBot, this);
        this.addListener(this.dispatcher, C_GameEvt.BOT_EXITED, this.removeBot, this);
        this.addListener(this.dispatcher, C_GameEvt.BOT_BALANCE_UPDATE, this.updateBotBalance, this);
        this.addListener(this.dispatcher, C_GameEvt.SHOW_GAME_OVER, this.onGameOver, this);
        this.addListener(this.dispatcher, C_GameEvt.POPUP_CLOSED, this.onPopupClosed, this);
    }


    showBonusScreenText(text)
    {
        let tf = this.assets.getTF(text, this.layout.relHeight(25));
        this.layout.horizontalAlignCenter(tf);
        this.layout.verticalAlignMiddle(tf);
        tf.alpha = 0;
        this.addChild(tf);

        TweenMax.to(tf, 0.5, {alpha: 1});
        TweenMax.to(tf, 0.5, {alpha: 0, delay:4.5});
    }


    playLaserAnim()
    {
        this.lasersAnim.alpha = 1;
        this.lasersAnim.gotoAndPlay(0);
    }


    onLaserLoop()
    {
        let rand = Math.random();
        if (rand < 0.1)
        {
            this.lasersAnim.stop();
            this.lasersAnim.alpha = 0;
            TweenMax.delayedCall(0.5 + 0.5*Math.random(), this.playLaserAnim.bind(this));
        }
    }


    addBot(name, position)
    {
        let cont = this.botContainers[position-1];
        let bg = cont.getChildAt(0);

        let botName = this.assets.getTF(name, this.layout.relHeight(25, bg), null, "#ffd800");
        cont.addChild(botName);
        botName.x = this.botsNamePos[position - 1];
        this.layout.verticalAlignTop(botName, 12.5, bg);

        this.botBalanceCont[position - 1].text = this.model.getBot(position).botBalance;

        cont.alpha = 1;
    }


    removeBot(position)
    {
        let cont = this.botContainers[position-1];

        let tf = cont.getChildAt(3);
        if (tf && tf.parent)
        {
            tf.parent.removeChild(tf);
        }
        cont.alpha = 0;
    }


    updateBotBalance(botId)
    {
        this.botBalanceCont[botId - 1].text = this.model.getBot(botId).botBalance;
    }


    onGoMenuConfirm(e)
    {
        // console.log(e.currentTarget.specialId);
        if (e)
        {
            e.stopPropagation();
        }
        this.model.pause();
        let msg = "Are you sure\nyou want to go to the main menu?";
        let popup = new ConfirmWin(this.onBack.bind(this), this.onResume.bind(this), msg);
        this.app.stage.addChild(popup);

        this.backOver = this.infoOver = this.popupOpened = false;
        this.popupOpened = true;
    }


    onPaytable(e)
    {
        if (e)
        {
            e.stopPropagation();
        }
        this.model.pause();
        this.controller.openPaytable();

        this.backOver = this.infoOver = this.popupOpened = false;
        this.popupOpened = true;
    }


    onSoundTrigger(e)
    {
        if (e)
        {
            e.stopPropagation();
        }

        this.sound.toggleMusic();
        this.sound.toggleSound();

        let soundSkin = this.localData.getBooleanValue(C_Local.SOUND_ENABLED, this.soundEnabled) ? "btnSound" : "btnSoundOff";
        this.soundButton.setSkins(soundSkin, soundSkin, soundSkin, soundSkin);
    }


    setCursor(value)
    {
        document.body.style.cursor = value;
    }


    onGameOver()
    {
        this.model.pause();
        this.cockpit.alpha = 0;

        this.destCockpitCont = new PIXI.Container();
        this.addChild(this.destCockpitCont);

        let w = this.layout.relWidth(100);
        let h = this.layout.relHeight(100);
        let shade = new PIXI.Graphics();
        shade.beginFill(0, .95);
        shade.drawRect(0, 0, w, h);
        shade.endFill();
        this.destCockpitCont.addChild(shade);

        let bg = [1, 2, 3, 4, 5];
        for (let i = 0; i < bg.length; i++)
        {
            let part = this.assets.getSprite("cockpit_destroyed_" + bg[i]);
            this.layout.propHeightScale(part, 100);
            this.destCockpitCont.addChild(part);
        }

        this.gameOverWin = new GameOverWin(this.onBuyCoinsRequest.bind(this), this.onBack.bind(this));
        this.app.stage.addChild(this.gameOverWin);

        // let overlay = new PIXI.Graphics();
        // overlay.beginFill(0xffffff, 1);
        // overlay.drawRect(-this.layout.relWidth(2.5), -this.layout.relHeight(2.5), this.layout.relWidth(105), this.layout.relHeight(105));
        // overlay.endFill();
        // overlay.alpha = 0;
        // this.addChild(overlay);
        //
        // TweenMax.to(overlay, 0.1, {alpha: 1});
        // TweenMax.to(overlay, 1.5, {alpha: 0, delay: 0.2, onComplete:this.destroyObj.bind(this), onCompleteParams:[overlay]});

    }


    onBuyCoinsRequest()
    {
        // TODO redirect for buying coins and move the below code in Success payment method
        this.model.unpause();
        this.destroyObj(this.destCockpitCont);
        this.cockpit.alpha = 1;
    }


    onResume()
    {
        this.popupOpened = false;
        this.setCursor("none");
        this.reticle.alpha = 1;
        this.model.unpause();
    }


    onBack(e)
    {
        if (e)
        {
            e.stopPropagation();
        }

        this.controller.goToRoomsMenu();
    }


    playRandomSound()
    {
        this.sound.playSound(C_Snd.RANDOM_SPACE_SOUNDS[MathUtil.getRandomInt(0, C_Snd.RANDOM_SPACE_SOUNDS.length - 1)], 0.5);
        let minDel = 5;
        let maxDel = 10;
        this.addDelayedCall(MathUtil.getRandomInt(minDel, maxDel), this.playRandomSound, [], this);
    }


    onPopupClosed()
    {
        this.popupOpened = false;
        this.setCursor("none");
        this.reticle.alpha = 1;

        // this.app.assets.removeDynamic();
        this.app.model.unpause();
    }


    onPlayerHit(velocity)
    {
        this.app.layout.shake(this, .2, this.shakeAmplitude, this.shakeAmplitude, .9, 0);
        this.sound.playSound(C_Snd.SHIELD_HIT);
        TweenMax.to(this.shield, .1, {alpha:1, overwrite:1});
        TweenMax.to(this.shield, .1, {alpha:0, delay:.1});
    }


    onBalanceUpdate()
    {
        this.balanceTF.text = this.model.getBalance();
        this.pointsWonTF.text = this.model.coinsWon;
    }


    onFlareKilled()
    {
        let overlay = new PIXI.Graphics();
        overlay.beginFill(0xffffff, 1);
        overlay.drawRect(-this.layout.relWidth(2.5), -this.layout.relHeight(2.5), this.layout.relWidth(105), this.layout.relHeight(105));
        overlay.endFill();
        overlay.alpha = 0;
        this.addChild(overlay);

        TweenMax.to(overlay, 0.1, {alpha: 1});
        TweenMax.to(overlay, 1.5, {alpha: 0, delay: 0.5, onComplete:this.destroyObj.bind(this), onCompleteParams:[overlay]});
    }


    onPodSpecialAnim(entity, killedBy)
    {
        let s = entity.getComponent(GameSprite);
        let entitySprite = s.sprite;

        let sprite = this.assets.getSprite(s.textureId);
        sprite.width = entitySprite.width;
        sprite.height = entitySprite.height;
        sprite.anchor.set(0.5);

        this.positionDisplayObjFromGameToScreen(entitySprite, sprite);

        let aspect = s.aspect;
        let newHeight = this.app.layout.relHeight(3);
        let newWidth = newHeight * aspect;

        this.addChild(sprite);
        TweenMax.to(sprite, 0.5, {width: newWidth, height: newHeight});

        let endX;
        let endY;
        if (killedBy == 0) // Player
        {
            endX = this.coin.x + newWidth/2;
            endY = this.coin.y + newHeight/2;
        }
        else // Either of the bots
        {
            let e = this.app.eSys.getBotEntityById(killedBy);

            if (e)
            {
                let fb = e.getComponent(FriendlyBot);

                endX = fb.coinImg.x + newWidth/2;
                endY = fb.coinImg.y + newHeight/2;
            }
            else
            {
                endX = sprite.x;
                endY = sprite.y;
            }
        }

        TweenMax.to(sprite, 1, {x: endX, y: endY, onComplete:this.destroyObj.bind(this), onCompleteParams:[sprite]});


    }


    onExplosion(entity)
    {
        let s = entity.getComponent(GameSprite);
        let entitySprite = s.sprite;

        let deathAnimFrames = this.app.assets.generateFrames("explosion_spaceship_", 2, 0, 4);
        let deathAnim = new PIXI.extras.AnimatedSprite(deathAnimFrames);
        deathAnim.anchor.set(0.5, 0.5);
        deathAnim.animationSpeed = 0.2;
        deathAnim.loop = false;
        deathAnim.onComplete = () => {
            deathAnim.parent.removeChild(deathAnim);
            deathAnim.destroy();
        };
        this.layout.propHeightScale(deathAnim, 300, entitySprite);
        deathAnim.x = entitySprite.x;
        deathAnim.y = entitySprite.y;
        deathAnim.sortZ = entitySprite.sortZ;

        this.mainContainer.addChild(deathAnim);
        // this.positionDisplayObjFromGameToScreen(entitySprite, deathAnim);

        deathAnim.gotoAndPlay(0);
    }


    positionDisplayObjFromGameToScreen(s, displayObj)
    {
        let ingameX = s.x;
        let ingameY = s.y;
        let globalPt = this.controller.getShootPt();
        let relPt = this.layout.globalToRelative(globalPt);
        let deltaX = relPt.x * 20;
        let deltaY = relPt.y * 20;

        let resultX = ingameX - (deltaX / 100) * this.layout.relWidth(100);
        let resultY = ingameY - (deltaY / 100) * this.layout.relHeight(100);

        displayObj.x = resultX;
        displayObj.y = resultY;
    }


    playCoinsAnimation(flierP, coinsWon, is_bomb = false, isBigWin = true) {
        let rewardCont = new PIXI.Container();
        this.addChild(rewardCont);

        if (isBigWin) {

            let rewardTF = this.assets.getTF(coinsWon, this.app.layout.relHeight(20));
            this.layout.horizontalAlignCenter(rewardTF);
            this.layout.verticalAlignMiddle(rewardTF);
            rewardTF.alpha = 0;
            rewardCont.addChild(rewardTF);

            TweenMax.to(rewardTF, 0.2, {alpha: 1});
            // TweenMax.to(rewardTF, 1, {tint: 0xff0000, yoyo: true});
            TweenMax.to(rewardTF, 0.2, {alpha: 0, delay: 1});
            TweenMax.to(rewardCont, 3, {onComplete: this.destroyObj.bind(this), onCompleteParams: [rewardCont]});

            return;
        }

        let worldPt = is_bomb ? new Point3D(0, 0, 0) : flierP.position.clone();
        let projPt = this.layout.projectGameToScreen(worldPt);
        let screenPos = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));

        let winBetRatio = coinsWon / this.model.getBet();
        let winType = is_bomb ? CoinsAnimations.BOMB_WIN :
            winBetRatio < 10 ? CoinsAnimations.SMALL_WIN :
                winBetRatio < 40 ? CoinsAnimations.NORMAL_WIN : CoinsAnimations.BIG_WIN;
        // winType = CoinsAnimations.BIG_WIN;
        let data = CoinsAnimations.coinData[winType];

        // COINS
        let coins = [];
        let coinsToSpawn = MathUtil.getRandomInt(data.coinsMin, data.coinsMax);
        for (let i = 0; i < coinsToSpawn; i++)
        {
            let coin = this.assets.getSprite("coin");
            this.app.layout.propHeightScale(coin, MathUtil.getRandomFloat(data.minSize, data.maxSize)); // TODO for landscape size - 3, 5
            coin.anchor.set(0.5, 0.5);
            coin.x = screenPos.x;
            coin.y = screenPos.y;
            coin.alpha = 0;
            rewardCont.addChild(coin);
            coins.push(coin);
        }

        // TEXT
        let textSize = this.layout.relHeight(4 + (winBetRatio*.2));
        let rewTf = this.assets.getTF(coinsWon, textSize);
        rewTf.anchor.set(0.5, 0.5);
        rewTf.x = screenPos.x;
        rewTf.y = screenPos.y;
        rewardCont.addChild(rewTf);

        if (winType == CoinsAnimations.SMALL_WIN)
        {
            this.createRingTween(coins, data);
            TweenMax.to(rewTf, 0.1, {alpha: 0, delay: 1.2});
        }
        else if (winType == CoinsAnimations.NORMAL_WIN)
        {
            this.createDoubleRingTween(coins, data);
            TweenMax.to(rewTf, 0.1, {alpha: 0, delay: 1.2});
        }
        else if (winType == CoinsAnimations.BIG_WIN || winType == CoinsAnimations.BOMB_WIN)
        {
            this.createStarTween(coins, data);
            TweenMax.to(rewTf, 0.1, {alpha: 0, delay: 1.2});
        }

        TweenMax.delayedCall(3, this.removeChild, [rewardCont], this);
    }


    createRingTween(coins, data)
    {
        let coinsToSpawn = coins.length;
        let rad = this.layout.relHeight(coinsToSpawn * .3);
        let angle = Math.PI*2 / coinsToSpawn;
        let spawnDur = data.spawnDuration;
        let coinSpawnRate = spawnDur / coinsToSpawn;

        let coin;
        for (let i = 0; i < coinsToSpawn; i++)
        {
            coin = coins[i];

            let spawnDelay = coinSpawnRate * i + 0.3;
            TweenMax.to(coin, 0, {alpha: 1, delay: spawnDelay});
            // TweenMax.to(coin, 0.3, {x: coin.x + i, y: coin + i, delay: 0.1 + spawnDelay});
            let radMultiplied = MathUtil.getRandomFloat(0.8, 1) * rad;
            TweenMax.to(coin, 0.3, {x: coin.x + Math.cos(angle*i)*radMultiplied,
                y: coin.y + Math.sin(angle*i)*radMultiplied,
                delay: spawnDelay});
            TweenMax.to(coin, 1.5, {x: this.coin.x + this.coin.width/2,  y: this.coin.y + this.coin.height/2,
                width: this.coin.width, height: this.coin.height, ease: Power2.easeOut, delay: 0.5 + spawnDur});
            TweenMax.to(coin, 1, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(1, 3), delay:spawnDelay});
            TweenMax.to(coin, 0.5, {rotation: 0, delay: 1 + spawnDelay});
            TweenMax.to(coin, 0, {alpha: 0, delay: 2 + spawnDur});
        }
    }

    createDoubleRingTween(coins, data)
    {
        let coinsToSpawn = coins.length;
        let halfCoins = coinsToSpawn/2;
        let rad1 = this.layout.relHeight(coinsToSpawn * .25);
        let rad2 = rad1 * 0.7;

        let angle = Math.PI*2 / (halfCoins);
        let spawnDur = data.spawnDuration;
        let coinSpawnRate = spawnDur / coinsToSpawn;

        let coin;
        for (let i = 0; i < coinsToSpawn; i++)
        {
            coin = coins[i];

            let spawnDelay = coinSpawnRate * i + 0.3;

            TweenMax.to(coin, 0, {alpha: 1, delay: spawnDelay});
            let radMultiplied;
            if (i < halfCoins)
            {
                radMultiplied = MathUtil.getRandomFloat(0.8, 1) * rad1;
            }
            else
            {
                radMultiplied = MathUtil.getRandomFloat(0.6, 1) * rad2;
            }

            TweenMax.to(coin, 0.3, {x: coin.x + Math.cos(angle*i)*radMultiplied,
                y: coin.y + Math.sin(angle*i)*radMultiplied,
                delay: spawnDelay});
            TweenMax.to(coin, 1.5, {x: this.coin.x + this.coin.width/2,  y: this.coin.y + this.coin.height/2,
                width: this.coin.width, height: this.coin.height, ease: Power2.easeOut,
                delay: 0.7 + spawnDur + MathUtil.getRandomFloat(0, 0.2)});
            let randRotScale = MathUtil.getRandomInt(1, 3);
            TweenMax.to(coin, 1, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(randRotScale - 0.5, randRotScale),
                delay:spawnDelay});
            TweenMax.to(coin, 0.5, {rotation: (Math.PI*2)*randRotScale, delay: 1 + spawnDelay});

        }
    }

    createStarTween(coins, data)
    {
        let coinsToSpawn = coins.length;

        let starHeight = this.layout.relHeight(10);
        let starWidth = starHeight*1.043;

        let positions = [
            {x: 0, y: -.55}, {x: 0, y: -.16}, {x: 0, y: .23},
            // left points
            {x: -.0433, y: -.42}, {x: -.0867, y: -.29}, {x: -.13, y: -.16},
            {x: -.2533, y: -.16}, {x: -.3767, y: -.16}, {x: -.5, y: -.16},
            {x: -.4, y: -.0833}, {x: -.3, y: -.0067}, {x: -.2, y: .07},
            {x: -.2367, y: .1967}, {x: -.2733, y: .3233}, {x: -.31, y: .45},
            {x: -.2067, y: .3767}, {x: -.1033, y: .3033},
            {x: -.16, y: -.05}, {x: -.11, y: .14},
            // right points
            {x: .0433, y: -.42}, {x: .0867, y: -.29}, {x: .13, y: -.16},
            {x: .2533, y: -.16}, {x: .3767, y: -.16}, {x: .5, y: -.16},
            {x: .4, y: -.0833}, {x: .3, y: -.0067}, {x: .2, y: .07},
            {x: .2367, y: .1967}, {x: .2733, y: .3233}, {x: .31, y: .45},
            {x: .2067, y: .3767}, {x: .1033, y: .3033},
            {x: .16, y: -.05}, {x: .11, y: .14},
        ];

        // positions = [
        //     // middle points:
        //     {x: 0, y: -.55}, {x: 0, y: -.16}, {x: 0, y: .23},
        //
        //     // left points
        //     {x: -.0433, y: -.42}, {x: -.8667, y: -.29}, {x: -.13, y: -.16},
        //     {x: -.2533, y: -.16}, {x: -.3767, y: -.16}, {x: -.5, y: -.16},
        //     {x: -.4, y: -.0833}, {x: -.3, y: -.0067}, {x: -.2, y: .07},
        //     {x: -.4, y: .1967}, {x: -.3, y: .3233}, {x: -.31, y: .45},
        //     {x: -.2067, y: .3767}, {x: -.1033, y: .3033},
        //     {x: -.16, y: -.05}, {x: -.11, y: .14},
        //
        //     // right points
        //     {x: .0433, y: -.42}, {x: .8667, y: -.29}, {x: .13, y: -.16},
        //     {x: .2533, y: -.16}, {x: .3767, y: -.16}, {x: .5, y: -.16},
        //     {x: .4, y: -.0833}, {x: .3, y: -.0067}, {x: .2, y: .07},
        //     {x: .4, y: .1967}, {x: .3, y: .3233}, {x: .31, y: .45},
        //     {x: .2067, y: .3767}, {x: .1033, y: .3033},
        //     {x: .16, y: -.05}, {x: .11, y: .14},
        // ];

        // Not working, need to download separately
        // let customEase =
        //     "M0,0,C0,0,0.009,0.073,0.056,0.12,0.106,0.17,0.225,0.166,0.31,0.246,0.352,0.285,0.289,0.418,0.334," +
        //     "0.464,0.399,0.53,0.605,0.536,0.678,0.598,0.745,0.654,0.761,0.73,0.806,0.784,0.913,0.913,1,1,1,1";

        let coin;
        let pos;
        for (let i = 0; i < coinsToSpawn; i++)
        {
            coin = coins[i];
            if (positions[i])
            {
                pos = positions[i];
            }

            let spawnDelay = MathUtil.getRandomFloat(0, 0.2) + 0.3;

            TweenMax.to(coin, 0, {alpha: 1, delay: spawnDelay});
            TweenMax.to(coin, 0.3, {x: coin.x + starWidth*pos.x, y: coin.y + starHeight*pos.y,
                delay: spawnDelay, ease: Power3.easeInOut});
            TweenMax.to(coin, 1.5, {x: this.coin.x + this.coin.width/2,  y: this.coin.y + this.coin.height/2,
                width: this.coin.width, height: this.coin.height, ease: Power2.easeOut,
                delay: 1.2 + MathUtil.getRandomFloat(0, 0.2)});
            let randRotScale = MathUtil.getRandomInt(1, 3);
            TweenMax.to(coin, 1, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(randRotScale - 0.5, randRotScale),
                delay:spawnDelay});
            TweenMax.to(coin, 0.5, {rotation: (Math.PI*2)*randRotScale, delay: 1 + spawnDelay});
        }
    }


    // createSpiralTween(coins, data)
    // {
    //     let coinsToSpawn = coins.length;
    //
    //     let maxR = this.layout.relHeight(7);
    //     let minR = this.layout.relHeight(5);
    //     let stepR = (maxR - minR) / coinsToSpawn;
    //
    //     let spawnDur = data.spawnDuration;
    //     let coinSpawnRate = spawnDur / coinsToSpawn;
    //
    //     let rad;
    //     let coin;
    //     for (let i = 0; i < coinsToSpawn; i++)
    //     {
    //         coin = coinsToSpawn[i];
    //
    //         coin.x = coin.x + Math.cos()*stepR*i;
    //
    //         let spawnDelay = coinSpawnRate * i;
    //         TweenMax.to(coin, 0, {alpha: 1, delay: spawnDelay});
    //         // TweenMax.to(coin, 0.3, {x: coin.x + Math.cos(angle*i)*rad,
    //         //     y: coin.y + Math.sin(angle*i)*rad,
    //         //     delay: spawnDelay});
    //         // TweenMax.to(coin, 1.5, {x: this.coin.x + this.coin.width/2,  y: this.coin.y + this.coin.height/2,
    //         //     width: this.coin.width, height: this.coin.height, ease: Power2.easeOut, delay: 0.5 + spawnDur});
    //         TweenMax.to(coin, 1, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(1, 3), delay:spawnDelay});
    //         TweenMax.to(coin, 0.5, {rotation: 0, delay: 1 + spawnDelay});
    //         TweenMax.to(coin, 0, {alpha: 0, delay: 2 + spawnDur});
    //     }
    // }


    playCoinsAnimation2(flierP, coinsWon, is_bomb = false, isBigWin = true)
    {
        let rewardCont = new PIXI.Container();
        this.addChild(rewardCont);

        if (isBigWin)
        {

            let rewardTF = this.assets.getTF(coinsWon, this.app.layout.relHeight(20));
            this.layout.horizontalAlignCenter(rewardTF);
            this.layout.verticalAlignMiddle(rewardTF);
            rewardTF.alpha = 0;
            rewardCont.addChild(rewardTF);

            TweenMax.to(rewardTF, 0.2, {alpha: 1});
            // TweenMax.to(rewardTF, 1, {tint: 0xff0000, yoyo: true});
            TweenMax.to(rewardTF, 0.2, {alpha: 0, delay: 1});
            TweenMax.to(rewardCont, 3, {onComplete:this.destroyObj.bind(this), onCompleteParams:[rewardCont]});

            return;
        }

        let worldPos = is_bomb ? new Point3D(0, 0, 0) : new Point3D(flierP.position.x, flierP.position.y, flierP.position.z);
        let screenPos = this.app.layout.projectGameToScreen(worldPos, 0);
        let spreadRad = this.app.layout.relHeight(10);
        if (is_bomb) spreadRad *= 2;
        let rad2 = spreadRad*spreadRad;


        // let totalCoins = MathUtil.getRandomInt(3, 6)*2;
        let totalCoins = is_bomb ? 60 : coinsWon*2/this.model.getBet();


        let coinsById = {};
        for(let i=0; i<totalCoins; i++)
        {
            let coin = this.app.assets.getSprite("coin");
            this.app.layout.propHeightScale(coin, MathUtil.getRandomFloat(2, 3)); // TODO for landscape size - 3, 5
            coin.anchor.set(0.5, 0.5);
            coin.x = screenPos.x * 5/6;
            coin.y = screenPos.y * 5/6;
            coin.alpha = 0;
            coinsById[i] = coin;
            rewardCont.addChild(coin);
        }

        let textSize = this.layout.relHeight(4 + (totalCoins*.2));
        let rewTf = this.assets.getTF(coinsWon, textSize);
        rewTf.anchor.set(0.5, 0.5);
        rewTf.x = coinsById[0].x;
        rewTf.y = coinsById[0].y;
        rewardCont.addChild(rewTf);

        if (this.app.eFactory.getViewType() == C_ViewType.TOP)
        {
            if (totalCoins%2 == 1) totalCoins--;
            let possibleXPos = totalCoins/2 + 1;
            let piDif = Math.PI/(totalCoins/2);
            let xPos = [];
            let xPiPos = [];
            let c = 0; // coin counter

            spreadRad *= (1.2 + totalCoins*.02);
            rad2 = spreadRad*spreadRad;

            for (let i = 0; i<possibleXPos; i++)
            {
                xPiPos[i] = Math.cos(piDif*i - Math.PI);
                xPos[i] = (spreadRad)*xPiPos[i];

                let y = 0;
                if (i != 0 && i != (possibleXPos-1))
                {
                    for (let k=0; k<2; k++) // 2 because you have +/- Y
                    {
                        y = (k%2==0) ? Math.sqrt(rad2 - Math.pow(xPos[i], 2)) : Math.sqrt(rad2 - Math.pow(xPos[i], 2))*(-1);
                        let spawnDelay = MathUtil.getRandomFloat(0, 0.2);
                        TweenMax.to(coinsById[c], 0, {alpha: 1, delay: spawnDelay});
                        TweenMax.to(coinsById[c], MathUtil.getRandomFloat(0.1, 0.3), {x: rewTf.x + xPos[i]*MathUtil.getRandomFloat(0.7, 1.1), y: rewTf.y + y*MathUtil.getRandomFloat(0.7, 1.1), delay: spawnDelay + 0.1});
                        // TweenMax.to(coinsById[c].scale, 0.15, {x: coinsById[c].scale.x*-1, repeat: 3, yoyo: true, delay:spawnDelay});
                        TweenMax.to(coinsById[c], 0.8, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(1, 3), delay:spawnDelay, ease: Power0.easeNone});
                        TweenMax.to(coinsById[c], 0.7, {rotation: 0, x: this.coin.x + coinsById[c].width/2, y: this.coin.y + coinsById[c].height/2, delay: 0.6 + spawnDelay});
                        c++;
                    }
                }
                else
                {
                    let spawnDelay = MathUtil.getRandomFloat(0, 0.2);
                    TweenMax.to(coinsById[c], 0, {alpha: 1, delay: spawnDelay});
                    TweenMax.to(coinsById[c], MathUtil.getRandomFloat(0.1, 0.3), {x: rewTf.x + xPos[i]*MathUtil.getRandomFloat(0.7, 1.1), y: rewTf.y + y*MathUtil.getRandomFloat(0.7, 1.1), delay: spawnDelay + 0.1});
                    // TweenMax.to(coinsById[c].scale, 0.15, {x: coinsById[c].scale.x*-1, repeat: 3, yoyo: true, delay:spawnDelay});
                    TweenMax.to(coinsById[c], 0.8, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(1, 3), delay:spawnDelay, ease: Power0.easeNone});
                    TweenMax.to(coinsById[c], 0.7, {rotation: 0, x: this.coin.x + coinsById[c].width/2, y: this.coin.y + coinsById[c].height/2, delay: 0.6 + spawnDelay});
                    c++;
                }
            }
        }
        else
        {
            let totalTime = .15 + 0.03*totalCoins;

            for (let i = 0; i<totalCoins; i++)
            {
                // let spawnDelay = i*0.05;
                let spawnDelay = (totalTime/totalCoins)*i + 0.3;
                let yMax = MathUtil.getRandomFloat(0.5, 1);
                let yMin = yMax*.25;
                let xEnd = MathUtil.getRandomFloat(-1, 1);
                // let xEnd = MathUtil.getRandomFloat(-1.5, 1.5);
                let xStart = xEnd*.1;
                TweenMax.to(coinsById[i], 0, {alpha: 1, delay: spawnDelay});
                TweenMax.to(coinsById[i], 0, {x: rewTf.x + spreadRad*xStart});
                TweenMax.to(coinsById[i], 0.6, {x: rewTf.x + spreadRad*xEnd, ease: Power1.easeOut, delay: spawnDelay});
                TweenMax.to(coinsById[i], 0.2, {y: rewTf.y - spreadRad*2*yMax, ease: Power2.easeOut, delay: spawnDelay});
                TweenMax.to(coinsById[i], 0.4, {y: rewTf.y - spreadRad*2*yMin, ease: Power2.easeOut, delay: spawnDelay + 0.25});
                TweenMax.to(coinsById[i], 0.8, {rotation: (Math.PI*2)*MathUtil.getRandomFloat(1, 3), delay:spawnDelay, ease: Power0.easeNone});
                TweenMax.to(coinsById[i], 0.7, {rotation: 0, x: this.coin.x + coinsById[i].width/2, y: this.coin.y + coinsById[i].height/2, delay: 0.8 + spawnDelay});
            }
        }

        TweenMax.to(rewTf, 0.2, {alpha: 0, delay: 1});
        TweenMax.to(rewardCont, 3, {onComplete:this.destroyObj.bind(this), onCompleteParams:[rewardCont]});
    }


    shoot(e)
    {
        if (this.app.model.isMobile())
        {
            // when you tap - you are in tap range in the beginning. Once you get out of it, its set to false
            this.inTapRange = true;
            this.startSwipeX = e.data.getLocalPosition(this).x;
            this.startSwipeY = e.data.getLocalPosition(this).y;
            this.updatePositionsAndRotations(e, true);
        }


        if(!this.tapped)
        {
            this.tapped = true;
            this.lastClickedTarget = e.target.flierId;
            setTimeout(() => {this.tapped = false}, 200);
        }
        else
        {
            this.tapped = false;
            if (e.target.flierId && e.target.flierId == this.lastClickedTarget)
            {
                this.controller.autoFireOn = true;
                this.controller.targetLocked = true;
                this.controller.lockTarget(e.target.flierId);
                console.log("Locked target: " + this.lastClickedTarget);
            }
            else
            {
                this.controller.n = false;
                this.controller.autoFireOn = !this.controller.autoFireOn;
                this.controller.targetLocked = false;
                this.controller.clearTargetLock();
                console.log("DOUBLE TAP! Auto-fire? : " + this.controller.autoFireOn);
            }

            this.showAutoFireNotif();
        }

        this.controller.startShooting(this.layout.getGlobalPtDiff(e.data.global, true));

        // Play shoot animations here:
        for (let i = 0; i < this.onShootAnimations.length; i++)
        {
            let anim = this.onShootAnimations[i];
            if (!anim.isPlaying)
            {
                anim.alpha = 1;
                anim.isPlaying = true;
                let startDelay = anim.startDelay ? anim.startDelay : 0;
                TweenMax.delayedCall(startDelay, anim.gotoAndPlay, [0], anim);
            }
        }
        for (let k = 0; k < this.onShootGlows.length; k++)
        {
            let glow = this.onShootGlows[k];
            if (!glow.isPlaying)
            {
                glow.isPlaying = true;
                // TweenMax.to(glow, glow.dur, {
                //     alpha: 1,
                //     repeat: -1,
                //     yoyo: true,
                //     repeatDelay: glow.repeatDelay});


                let tl = new TimelineMax({repeat: -1, repeatDelay: glow.repeatDelay});
                tl.add(TweenMax.to(glow, glow.dur*.5, {alpha: 1}), 0);
                tl.add(TweenMax.to(glow, glow.dur*.5, {alpha: 0}), glow.dur*.5);
                tl.play();
            }
        }


        // if (e.currentTarget != this.backButton && e.currentTarget != this.paytableButton)
        // {
        //     // this.controller.startShooting(e.data.global);
        //     this.controller.startShooting(this.layout.getGlobalPtDiff(e.data.global));
        // }
    }


    onShoot(weapon)
    {
        if (this.weapons[weapon])
        {
            this.weapons[weapon].getChildAt(0);
            this.weapons[weapon].getChildAt(1);

            TweenMax.to(this.weapons[weapon].getChildAt(1), 0, {alpha: 0});
            TweenMax.to(this.weapons[weapon].getChildAt(0), 0, {alpha: 1});
            TweenMax.to(this.weapons[weapon].getChildAt(1), 0, {alpha: 1, delay: 0.05});
            TweenMax.to(this.weapons[weapon].getChildAt(0), 0, {alpha: 0, delay: 0.05});
        }
    }


    stopShooting()
    {
        if (!this.controller.autoFireOn && !this.controller.targetLocked)
        {
            this.controller.stopShooting();
            this.showAutoFireNotif();

            // stop shoot animations
            for (let i = 0; i < this.onShootAnimations.length; i++)
            {
                let anim = this.onShootAnimations[i];
                anim.alpha = 0;
                anim.gotoAndStop(0);
                anim.isPlaying = false;
                TweenMax.killDelayedCallsTo(anim.gotoAndPlay);
                TweenMax.killTweensOf(anim);
            }
            for (let k = 0; k < this.onShootGlows.length; k++)
            {
                let glow = this.onShootGlows[k];
                glow.isPlaying = false;
                TweenMax.killTweensOf(glow);
                glow.alpha = 0;
            }
        }
    }


    updatePositionsAndRotations(e, tap = false)
    {
        // let globalPt = e.data.global;
        if (this.app.model.isMobile() && this.inTapRange)
        {
            let deltaSwipeX = Math.abs(e.data.getLocalPosition(this).x - this.startSwipeX);
            let deltaSwipeY = Math.abs(e.data.getLocalPosition(this).y - this.startSwipeY);
            if (deltaSwipeX > this.layout.relWidth(2) || deltaSwipeY > this.layout.relHeight(2))
            {
                this.inTapRange = false;
                tap = false;
            }
            else
            {
                tap = true;
            }
        }

        let globalPt = this.layout.getGlobalPtDiff(e.data.global, tap);

        let relPt = this.layout.globalToRelative(globalPt);

        let retX = this.layout.relWidth(relPt.x*100);
        let retY = this.layout.relHeight(relPt.y*100);
        if (this.app.model.isMobile())
        {
            TweenMax.to(this.reticle, 0.3, {x: retX, y: retY, ease:Cubic.easeOut});
        }
        else
        {
            this.reticle.x = retX;
            this.reticle.y = retY;
        }

        let targetX = this.deltaX * relPt.x;
        let targetY = this.deltaY * relPt.y;

        TweenMax.allTo([this.mainContainer, this.bg], .3, {x:targetX , y:targetY, ease:Cubic.easeOut});

        this.reticleGlobalPos = this.toGlobal(new PIXI.Point(retX, retY));

        this.controller.updateShootPt(globalPt);
        this.controller.updateWeaponsRotation(globalPt);
    }


    getReticlePosToLocal()
    {
        return this.toLocal(this.reticleGlobalPos);
    }


    /**
     *
     * @param e {Entity}
     */
    // playBeamWeaponAnim(e, isAccurate = true)
    // {
    //     let p = e.getComponent(Physics);
    //
    //     let projPt = this.layout.projectGameToScreen(p.position);
    //     // when hitting directly a target
    //     let localPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));
    //     // when shooting in the space
    //     if (!isAccurate)
    //     {
    //         localPt = this.toLocal(this.reticleGlobalPos);
    //     }
    //     let startPt = new Point3D(localPt.x, localPt.y, 0);
    //
    //     let endPt = new Point3D(this.layout.relWidth(50), this.layout.relHeight(87), 0);
    //
    //     let beamSprite1 = this.getBeam(endPt, startPt);
    //
    //     //beamSprite.alpha = 0;
    //     let duration = .15;//isAccurate ? .15 : .075;
    //
    //     TweenMax.from(beamSprite1.scale, duration, {y:0});
    //     TweenMax.delayedCall(duration, this.moveBeamPivot, [beamSprite1], this);
    //     TweenMax.to(beamSprite1.scale, duration, {delay:duration, y:0});
    //
    //     //TweenMax.from(beamSprite1, duration, {alpha:0, yoyo:true, repeat:1});
    //     TweenMax.delayedCall(duration*2, this.removeChild, [beamSprite1], this);
    //
    //     // TODO add second beam from the end point of the first if not hitting a target
    //     if (!isAccurate)
    //     {
    //         let secondBeamEndPt = new Point3D(localPt.x, localPt.y, 0);
    //         let secondBeamLocalPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));
    //         let secondBeamStartPt = new Point3D(secondBeamLocalPt.x, secondBeamLocalPt.y, 0);
    //
    //         let beamSprite2 = this.getBeam(secondBeamEndPt, secondBeamStartPt);
    //
    //         TweenMax.from(beamSprite2.scale, duration, {y:0, delay: duration});
    //         TweenMax.delayedCall(duration*2, this.moveBeamPivot, [beamSprite2], this);
    //         TweenMax.to(beamSprite2.scale, duration, {y:0, delay: duration*2});
    //
    //         //TweenMax.from(beamSprite2, duration, {alpha:0, yoyo:true, repeat:1});
    //
    //         TweenMax.delayedCall(duration*3, this.removeChild, [beamSprite2], this);
    //     }
    // }


    // moveBeamPivot(beam)
    // {
    //     let rot = beam.rotation;
    //     let move = beam.height * .852;
    //     beam.x += Math.sin(rot) * move;
    //     beam.y -= Math.cos(rot) * move;
    //
    //     beam.anchor.set(.463, .078);
    // }


    // getBeam(endPt, startPt)
    // {
    //     let beamSprite = this.assets.getSprite("lightning");
    //     beamSprite.anchor.set(.49, .93);
    //
    //     let beamVector = endPt.subtract(startPt);
    //     beamSprite.scale.y = beamVector.getLength() * 1.2 / beamSprite.height;
    //     beamSprite.rotation = Math.atan2(beamVector.y, beamVector.x) - Math.PI * .5;
    //
    //     this.addChildAt(beamSprite, this.getChildIndex(this.cockpit));
    //     beamSprite.x = endPt.x;
    //     beamSprite.y = endPt.y;
    //
    //     if (Math.random() < .5)
    //     {
    //         beamSprite.scale.x *= -1;
    //     }
    //     return beamSprite;
    // }

    /*
    playMultiSpriteBeamAnim(e, relStartPos, isAccurate, botId)
    {
        let duration = .3;
        let p = e.getComponent(Physics);

        let projPt = this.layout.projectGameToScreen(p.position);
        let localPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));

        if (!isAccurate && !botId)
        {
            localPt = this.toLocal(this.reticleGlobalPos);
            //duration *= .5;
        }

        let startPt = new Point3D(this.layout.relWidth(relStartPos.x), this.layout.relHeight(relStartPos.y), 0);
        let endPt = new Point3D(localPt.x, localPt.y, 0);

        let beamDistance = this.layout.relHeight(0.75);

        let beamVector1 = endPt.subtract(startPt);
        let totalHeight1 = beamVector1.getLength();
        let totalProjectiles1 = Math.ceil(totalHeight1 / beamDistance);

        let maxHeight = 3;
        let minHeight = 2;
        let totalBeamProjectiles = totalProjectiles1;

        if (!isAccurate && !botId)
        {
            // let secondBeamEndPt = new Point3D(localPt.x, localPt.y, 0);
            // let secondBeamLocalPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));
            // let secondBeamStartPt = new Point3D(secondBeamLocalPt.x, secondBeamLocalPt.y, 0);

            let startPt2 = new Point3D(localPt.x, localPt.y, 0);
            let localPt2 = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));
            let endPt2 = new Point3D(localPt2.x, localPt2.y, 0);

            let beamVector2 = endPt2.subtract(startPt2);
            let totalHeight2 = beamVector2.getLength();
            let totalProjectiles2 = Math.ceil(totalHeight2 / beamDistance);

            totalBeamProjectiles += totalProjectiles2;

            let heightDif = maxHeight - minHeight;
            let heightScaling = heightDif/totalBeamProjectiles;
            let midHeight = maxHeight - heightScaling*totalProjectiles1; // the Height at the point between the two vectors

            let dur1 = duration * (totalProjectiles1 / totalBeamProjectiles);
            let lastIndex = this.getBeamAnim(beamVector1, totalProjectiles1,
                startPt, duration, dur1, 0, midHeight, maxHeight);

            let dur2 = duration * (totalProjectiles2 / totalBeamProjectiles);
            this.getBeamAnim(beamVector2, totalProjectiles2,
                startPt2, duration, dur2, lastIndex, minHeight, midHeight);
        }
        else
        {
            this.getBeamAnim(beamVector1, totalProjectiles1,
                startPt, duration, duration, 0, minHeight, maxHeight);
        }
    }
    */


    playMultiSpriteBeamAnim(pos, relStartPos, isAccurate, botId, locked)
    {
        let duration = .3;
        // let p = e.getComponent(Physics);

        let projPt = this.layout.projectGameToScreen(pos);
        let localPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));

        // if (!isAccurate && !botId)
        // {
        //     localPt = this.toLocal(this.reticleGlobalPos);
        // }
        if (!isAccurate && !botId)
        {
            localPt = this.toLocal(this.reticleGlobalPos);
        }

        let startPt = new Point3D(this.layout.relWidth(relStartPos.x), this.layout.relHeight(relStartPos.y), 0);
        let endPt = new Point3D(localPt.x, localPt.y, 0);

        let beamDistance = this.layout.relHeight(0.75);

        let beamVector1 = endPt.subtract(startPt);
        let totalHeight1 = beamVector1.getLength();
        let totalProjectiles1 = Math.ceil(totalHeight1 / beamDistance);

        let maxHeight = 3;
        let minHeight = 2;

        let texPostfix = "";
        // if (botId == 1) texPostfix = "_left";
        // else if (botId == 2) texPostfix = "_right";
        if (botId == 1) texPostfix = "_left8";
        else if (botId == 2) texPostfix = "_right";

        this.getBeamAnim(beamVector1, totalProjectiles1, startPt, duration, duration, 0, minHeight, maxHeight, texPostfix);

        let hoverBall = null;
        if (!isAccurate && !botId)
        {
            hoverBall = this.assets.getSprite("cockpit_1_fireball2");
            this.layout.propHeightScale(hoverBall, 6);
            hoverBall.anchor.set(.5, .5);
            hoverBall.blendMode = PIXI.BLEND_MODES.ADD;
            this.addChildAt(hoverBall, this.getChildIndex(this.botsContainer) - 1);
            hoverBall.x = endPt.x;
            hoverBall.y = endPt.y;

            let delay = .2;
            TweenMax.from(hoverBall, .1, {alpha:0, delay: delay});
            let s = hoverBall.scale.x;
            TweenMax.to(hoverBall.scale, .2, {x: s * .8, y:s * .8, yoyo:true, repeat: -1});
            TweenMax.to(hoverBall, .5, {rotation:Math.PI * 2, repeat: -1});
        }

        return hoverBall;
    }


    /**
     *
     * @param hoverBall {PIXI.Sprite}
     * @param targetPhysics {Physics}
     */
    finishBeamAnim(hoverBall, targetPhysics)
    {
        TweenMax.killTweensOf(hoverBall);
        TweenMax.to(hoverBall, .1, {alpha:0});
        TweenMax.delayedCall(.1, this.removeChild, [hoverBall], this);

        let duration = .3;

        let projPt = this.layout.projectGameToScreen(targetPhysics.position);
        let localPt = this.toLocal(this.mainContainer.toGlobal(new PIXI.Point(projPt.x, projPt.y)));

        let startPt = new Point3D(hoverBall.x, hoverBall.y, 0);
        let endPt = new Point3D(localPt.x, localPt.y, 0);

        let beamDistance = this.layout.relHeight(0.75);

        let beamVector = endPt.subtract(startPt);
        let totalHeight = beamVector.getLength();
        let totalProjectiles = Math.ceil(totalHeight / beamDistance);

        this.getBeamAnim(beamVector, totalProjectiles, startPt, duration, duration, 0, 2, 3);
    }


    getBeamAnim(beamVector, totalProjectiles, startPt, totalDur, baseDur, lastAnimIndex, minHeight, maxHeight, texPostfix = "")
    {
        let baseDelay = (baseDur/totalProjectiles) * .5;

        let heightDif = maxHeight - minHeight;
        let heightScaling = heightDif/totalProjectiles;

        for (let prjIdx = 0 ; prjIdx <= totalProjectiles; prjIdx++)
        {
            let h = maxHeight - heightScaling*prjIdx;

            let fire = this.assets.getSprite("cockpit_1_fireball2" + texPostfix);
            this.layout.propHeightScale(fire, h);
            fire.anchor.set(.5, .5);
            fire.blendMode = PIXI.BLEND_MODES.ADD;

            this.addChildAt(fire, this.getChildIndex(this.botsContainer) - (prjIdx + lastAnimIndex));

            let spriteVector = beamVector.clone();
            spriteVector.scale(prjIdx/totalProjectiles);
            let pos = startPt.add(spriteVector);

            let xOff = fire.width * MathUtil.getRandomFloat(0.05, 0.1);
            xOff *= Math.random() > 0.5 ? 1 : -1;
            let xTo = xOff*(-1);
            fire.x = pos.x + xOff;
            fire.y = pos.y;

            let delay = baseDelay * (prjIdx + lastAnimIndex);
            TweenMax.from(fire, 0.5/8, {alpha:0, yoyo:true, repeat:1, delay: delay});
            TweenMax.to(fire, 0.5/8, {x: pos.x + xTo, yoyo: true, repeat: 2});
            TweenMax.to(fire, 0.5/2, {rotation: Math.random()*Math.PI*4, delay: delay});
            TweenMax.delayedCall(delay + totalDur, this.removeChild, [fire], this);
        }

        return totalProjectiles;
    }


    destroy()
    {
        this.app.eSys.stopSystem();
        super.destroy();
    }


}

export default MainView